import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { findExercise } from "@/entities/exercise/model/exercises";
import type { Exercise } from "@/entities/exercise/model/exercise-types";
import { useNumberSetting } from "@/shared/hooks/use-setting";
import { useWorkouts } from "@/entities/workout/api/use-workouts";
import { TrainingMode } from "@/features/training-mode/ui/training-mode";

export const Route = createFileRoute("/training")({
  component: TrainingPage,
});

function TrainingPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { activeWorkout } = useWorkouts();
  const { value: workSeconds } = useNumberSetting("trainingWorkSeconds", 45);
  const { value: restSeconds } = useNumberSetting("trainingRestSeconds", 90);

  const exercises =
    activeWorkout?.exerciseIds
      .map((id) => findExercise(id))
      .filter((exercise): exercise is Exercise => exercise !== undefined) ?? [];

  const handleClose = (): void => {
    void navigate({ to: "/" });
  };

  if (exercises.length === 0) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-2xl font-semibold">No active workout</h1>
        <p className="max-w-md text-sm text-[var(--color-muted-foreground)]">
          Select a workout with exercises in the sidebar before starting a training session.
        </p>
        <button
          type="button"
          onClick={handleClose}
          className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm hover:bg-[var(--color-muted)]"
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-[var(--color-background)] text-[var(--color-foreground)]">
      <TrainingMode
        exercises={exercises}
        workoutId={activeWorkout?.id ?? null}
        workoutName={activeWorkout?.name ?? "Workout"}
        workSeconds={workSeconds}
        restSeconds={restSeconds}
        onClose={handleClose}
      />
    </div>
  );
}
